/**
 * Product Versions Config — source of truth สำหรับ version ของแต่ละ product
 * Deploy แล้ว app จะ sync กับ Firestore ให้อัตโนมัติ
 *
 * 📖 กฎการ bump + format ของ note ดูใน firebase/web/VERSIONING.md
 *
 * Quick reference:
 *   PATCH — bug fix, refactor, UI ปรับเล็กน้อย
 *   MINOR — feature ใหม่ (หน้า/ปุ่ม/column/filter/report)
 *   MAJOR — breaking change (API/DB/flow/redesign ใหญ่)
 *
 * Note = Production Release Notes (ไม่ใช่ git commit):
 *   เขียนเหมือน release notes บน App Store / Play Store
 *   ลูกค้าอ่านแล้วเข้าใจว่า "มีอะไรใหม่ / ทำไมต้องอัปเดต / ส่งผลกับฉันยังไง"
 *
 *   - PATCH เล็ก → headline 1 บรรทัด
 *   - PATCH ที่มี impact → headline + intro paragraph
 *   - MINOR → headline + intro + bullet "จุดเด่นของการอัปเดต"
 *   - MAJOR / regulatory → ครบทุก section + "สิ่งที่ผู้ใช้ต้องทำ"
 *
 * CLI:
 *   npm run bump -- patch isEtax "ปรับถ้อยคำหน้าแจ้งเตือนการเชื่อมต่อให้เข้าใจง่ายขึ้น"
 *
 * Multi-line note: เปิดไฟล์นี้แล้วใช้ template literal (`...`) ตรงๆ
 * เพราะ CLI ยังไม่รองรับ multi-line argument
 */

export default {
  isEtax: {
    version: '1.5.2',
    updatedAt: '2026-05-29',
    note: 'ปรับถ้อยคำหน้าแจ้งเตือนการเชื่อมต่ออินเทอร์เน็ตให้เป็นธรรมชาติและเข้าใจง่ายขึ้น ไม่กระทบการทำงานของระบบ',
  },
  isPdfDoc: {
    version: '1.0.0',
    updatedAt: '2026-04-10',
    note: 'เปิดให้บริการ PDF Doc ครั้งแรก รองรับการสร้างและจัดการเอกสาร PDF ในระบบ',
  },
  isApAutomate: {
    version: '1.5.6',
    updatedAt: '2026-05-06',
    note: `รวม flow เอกสาร pending ของ Sanrio เข้ากับ flow หลัก พร้อมเพิ่มสถานะ "RESTORE_PENDING" สำหรับการกู้คืนเอกสาร

ในเวอร์ชันก่อนหน้า เอกสาร pending ของลูกค้า Sanrio ใช้ flow แยกที่ทำงานคู่ขนานกับ flow หลัก ทำให้การติดตามและรายงานต้องดูคนละจุด ในเวอร์ชันนี้ระบบรวมทุก variant เข้าใน flow เดียวกัน เพื่อให้การจัดการเอกสาร AP ทั้งหมดสอดคล้องและตรวจสอบง่ายขึ้น

จุดเด่นของการอัปเดต:
• เอกสาร AP pending ทั้งหมดอยู่ในมุมมองเดียวกัน ไม่แยกตามลูกค้าอีกต่อไป
• เพิ่มสถานะ "RESTORE_PENDING" สำหรับกรณีต้องการกู้คืนเอกสารที่เคยถูกปฏิเสธหรือยกเลิก
• รายงานและตัวกรองในหน้า Accounts Payable ครอบคลุมเอกสารจากทุก variant`,
  },
  isLab: {
    version: '1.2.0',
    updatedAt: '2026-08-25',
    note: `กำหนดวันหมดอายุการใช้งาน AP Lab ได้รายบริษัท และแจ้งเตือนก่อนหมดอายุ

ก่อนหน้านี้บริษัทที่เปิดใช้งาน AP Lab จะใช้งานได้ไม่มีกำหนด แม้คอร์สอบรมจะสิ้นสุดไปแล้ว ทำให้ทีมงานต้องไล่ปิดการใช้งานเองทีละบริษัท ในเวอร์ชันนี้บริษัทที่สร้างจากหน้า Laboratory จะได้รับวันหมดอายุตั้งแต่ตอนสร้าง โดยค่าเริ่มต้นคือ 1 เดือน และต่ออายุได้ภายหลังจากหน้าจัดการผลิตภัณฑ์

จุดเด่นของการอัปเดต:
• กำหนดวันหมดอายุ license ได้ตั้งแต่ตอนสร้างบริษัทในหน้า Laboratory ค่าเริ่มต้น 1 เดือน
• ต่ออายุหรือยกเลิกวันหมดอายุได้จากหน้าจัดการผลิตภัณฑ์ พร้อมปุ่มลัด +1 เดือน / +3 เดือน / +1 ปี
• หน้าจัดการผลิตภัณฑ์แสดงจำนวนวันที่เหลือ และเน้นสีเมื่อหมดอายุแล้ว
• เมื่อเลยวันหมดอายุ ผู้ใช้ของบริษัทนั้นจะเข้าใช้งานไม่ได้ และเห็นหน้าแจ้งเหตุผลพร้อมช่องทางติดต่อ
• เอกสารที่หลุดเข้ามาหลังหมดอายุจะไม่ถูกประมวลผลต่อ จึงไม่มีค่าใช้จ่าย AI เกิดขึ้นย้อนหลัง

สิ่งที่ผู้ใช้ต้องทำ: บริษัท AP Lab ที่มีอยู่เดิมยังใช้งานได้ตามปกติ เพราะยังไม่มีวันหมดอายุกำกับไว้ หากต้องการให้หมดอายุ กรุณากำหนดวันที่ในหน้าจัดการผลิตภัณฑ์ของบริษัทนั้น`,
  },
  isExchangeRate: {
    version: '1.0.0',
    updatedAt: '2026-04-10',
    note: 'เปิดให้บริการ Exchange Rate ครั้งแรก สำหรับดึงและจัดการอัตราแลกเปลี่ยนเงินตราต่างประเทศจากแหล่งข้อมูลทางการ',
  },
  isNsThaiSmartTax: {
    version: '1.4.2',
    updatedAt: '2026-08-06',
    note: `สงวนปุ่ม "ยกเลิกรายการ" ในหน้าภาษีหัก ณ ที่จ่าย ให้เฉพาะทีมงานผู้ดูแลระบบเท่านั้น

ในเวอร์ชันก่อนหน้า ผู้ใช้ที่มีสิทธิ์ผู้ดูแลของบริษัทสามารถกดปุ่ม "ยกเลิกรายการ" เพื่อเปลี่ยนสถานะเอกสารหัก ณ ที่จ่ายที่เลือกไว้เป็น Cancel ได้เอง ซึ่งเป็นการเปลี่ยนแปลงที่ย้อนกลับจากหน้าจอไม่ได้ ในเวอร์ชันนี้ปุ่มดังกล่าวแสดงเฉพาะบัญชีของทีมงานผู้ดูแลระบบ เพื่อลดความเสี่ยงที่เอกสารจะถูกยกเลิกโดยไม่ตั้งใจ ส่วนการค้นหา การส่งออก Excel และการใช้งานอื่นในหน้านี้ยังทำงานเหมือนเดิมทุกประการ

สิ่งที่ผู้ใช้ต้องทำ: หากต้องการยกเลิกเอกสารหัก ณ ที่จ่าย กรุณาแจ้งทีมงานผู้ดูแลระบบเพื่อดำเนินการให้`,
  },
  isNsThaiSmartTaxAverage: {
    version: '1.0.0',
    updatedAt: '2026-04-10',
    note: 'เปิดให้บริการระบบภาษีซื้อเฉลี่ยครั้งแรก สำหรับธุรกิจที่มีรายได้ทั้งจากกิจการที่ต้องเสียและไม่ต้องเสียภาษีมูลค่าเพิ่ม รองรับการคำนวณภาษีซื้อเฉลี่ยตามอัตราส่วนรายได้',
  },
  isNsThaiSmartPnd54Pp36: {
    version: '1.3.1',
    updatedAt: '2026-06-04',
    note: `แก้ไขหน้ายกเลิกรายการ ภ.ง.ด.54 ให้แสดงเฉพาะเอกสาร ภ.ง.ด.54 ไม่ปนกับเอกสารหัก ณ ที่จ่ายปกติ

ในเวอร์ชันก่อนหน้า หน้ายกเลิกรายการ ภ.ง.ด.54 จะดึงเอกสารหัก ณ ที่จ่ายทุกประเภทมาแสดงรวมกัน ทำให้ผู้ใช้ต้องคัดกรองด้วยตาเพื่อหาเอกสาร ภ.ง.ด.54 ที่ต้องการยกเลิก ในเวอร์ชันนี้ระบบกรองเฉพาะเอกสารที่มี taxWHCode=3 ให้แล้ว เพื่อให้ตรงกับขอบเขตของหน้านี้

สิ่งที่ผู้ใช้ต้องทำ: ไม่ต้องทำอะไรเพิ่ม รายการที่แสดงในหน้านี้จะถูกต้องโดยอัตโนมัติ`,
  },
  isTopserv: {
    version: '1.0.0',
    updatedAt: '2026-04-10',
    note: 'เปิดให้บริการเชื่อมต่อระบบ Topserv ครั้งแรก รองรับการรับ-ส่งเอกสารระหว่างระบบบัญชีกับ Topserv',
  },
  isFixedAssetCount: {
    version: '1.0.0',
    updatedAt: '2026-04-10',
    note: 'เปิดให้บริการระบบนับสินทรัพย์ถาวร (Fixed Asset Count) ครั้งแรก รองรับการจัดทำใบนับสินทรัพย์ ตรวจสอบสถานะรายชิ้น และออกรายงานสรุปผลการนับ',
  },
  isLaoTaxRis: {
    version: '1.1.0',
    updatedAt: '2026-08-13',
    note: `เพิ่มปุ่ม "ส่งซ้ำ" สำหรับเอกสารที่ค้างอยู่ในคิวนำส่ง Lao eTax

ในเวอร์ชันก่อนหน้า หากการนำส่งสะดุดระหว่างทาง เอกสารจะค้างที่สถานะ "เข้าคิว" หรือ "กำลังส่ง" ไปเรื่อย ๆ โดยผู้ใช้กดส่งใหม่เองไม่ได้ ต้องแจ้งทีมงานให้แก้ไขให้ ในเวอร์ชันนี้ระบบจะตรวจพบเอกสารที่ค้างเกิน 15 นาที แล้วเปิดปุ่มส่งซ้ำให้ผู้ใช้กดได้เอง การส่งซ้ำใช้เลขอ้างอิงเดิมเสมอ จึงไม่ทำให้เกิดใบกำกับภาษีซ้ำในระบบ e-Tax ของประเทศลาว

จุดเด่นของการอัปเดต:
• ปุ่ม "ส่งซ้ำ" แสดงอัตโนมัติเมื่อเอกสารค้างสถานะ "เข้าคิว" หรือ "กำลังส่ง" นานเกิน 15 นาที
• ตั้งค่าโหมดทดสอบ (DEV) และโหมดระบบจริง (PROD) แยกได้รายบริษัท และมีผลทันทีโดยไม่ต้องรอรอบติดตั้งระบบ
• ผลการทดสอบการเชื่อมต่อแสดงป้ายกำกับโหมดที่ใช้อยู่ เพื่อยืนยันก่อนส่งเอกสารจริงว่ากำลังต่อกับระบบใด
• โหมดข้อมูลจำลองสงวนไว้สำหรับทีมงานผู้ดูแลระบบเท่านั้น และถูกปิดกั้นไม่ให้ส่งเข้าระบบจริงในทุกกรณี
• จำกัดสิทธิ์การนำส่งและการยกเลิกใบกำกับภาษี ให้เฉพาะผู้ใช้ที่มีสิทธิ์บันทึกเอกสารของบริษัทนั้น
• บันทึกประวัติการนำส่งทุกครั้ง ทั้งผู้ส่ง เวลา ข้อมูลที่ส่ง และผลตอบกลับจากระบบ e-Tax ของประเทศลาว สำหรับตรวจสอบย้อนหลัง

สิ่งที่ผู้ใช้ต้องทำ: ไม่ต้องทำอะไรเพิ่ม ปุ่มและป้ายกำกับใหม่จะแสดงในหน้ารายการเอกสารโดยอัตโนมัติ สำหรับบริษัทที่จะเริ่มนำส่งเข้าระบบจริง กรุณาติดต่อทีมงานเพื่อตั้งค่าโหมด PROD ก่อนเริ่มใช้งาน`,
  },
}
